import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Box, FormControl, Select, MenuItem, Card, CardContent, Button, LinearProgress, Divider } from '@mui/material';
import ItemGrid from '../common/ItemGrid';
import MyLearningCard from './MyLearningCard';
import MyLearningListView from './MyLearningListView';
import { learnerService } from '../../services/api';
import { useContentPlayer } from '../../context/ContentPlayerContext';
import { useEmbedded } from '../../hooks/useEmbedded';
import { useEmbedParams } from '../../hooks/useEmbedParams';
import { tokens } from '../../theme';
import LoadingSkeleton from '../common/LoadingSkeleton';
import TypeTag from '../shared/TypeTag';

const SearchContentCard = ({ item, onOpen }) => {
  const contentType = item.type
    ? item.type.charAt(0).toUpperCase() + item.type.slice(1)
    : 'Content';

  // Remove HTML tags from description
  const plainDescription = (item.description || 'No description available.')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

  return (
    <div className="recommendation-card-wrapper">
      <Card
        className="recommendation-card"
        onClick={() => onOpen(item.id)}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: `1px solid ${tokens.color.borderSecondary}`,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
          },
        }}
      >
        <CardContent
          className="recommendation-card-content"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            pb: 1,
          }}
        >
          {/* Content type tag - match course card */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TypeTag type={contentType} />
          </Box>

          {/* Title */}
          <Typography
            variant="subtitle2"
            component="h3"
            className="recommendation-card-title"
            sx={{
              fontWeight: 600,
              pb: 1,
              mb: 1.25,
              borderBottom: "0.5px solid rgba(0,0,0,0.10)",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.3,
            }}
          >
            {item.name || item.title || 'Content'}
          </Typography>
          {/* Description without HTML tags */}
          <Box
            sx={{
              height: "32px",
              mb: 1,
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            {item.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                className="recommendation-card-desc"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                  wordBreak: "break-word",
                  hyphens: "auto",
                }}
                dangerouslySetInnerHTML={{
                  __html: plainDescription
                    ?.replace(/<\/?p>/g, "")
                    .replace(/<[^>]*>/g, ""),
                }}
              />
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }} />

          {/* Progress bar layout - match course card */}
          <Box sx={{ mt: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={100} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 3, 
                    backgroundColor: tokens.color.bgTertiary, 
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 100 >= 100 ? tokens.color.positive : tokens.color.brand,
                      }, 
                  }} /> 
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.textSecondary, minWidth: 36, textAlign: 'right', }} > 100% </Typography> 
            </Box>
          </Box>
        </CardContent>

        {/* Button */}
        <Box sx={{
          mt: 2,
          pt: 0,
          display: "flex",
          justifyContent: "flex-start", 
          alignItems: "flex-end",      
          width: "100%", }}>
          <Button
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(item.id);
            }}
            size="small"
            color="primary"
            sx={{
              px: 1.5,
              py: 0.5,
              minHeight: "28px",
              width: "100%",
            }}
          >
            Open
          </Button>
        </Box>
      </Card>
    </div>
  );
};

const MyLearnings = () => {
  const [learnings, setLearnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentSearchContent, setRecentSearchContent] = useState([]);
  const [recentContentLoading, setRecentContentLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const isEmbedded = useEmbedded();
  const embedParams = useEmbedParams();
  const { openContentPlayer } = useContentPlayer();


  const fetchItems = useCallback(async (page, limit, searchTerm, sortOrder) => {
    const [sortField, sortDirection] = sortOrder ? sortOrder.split(' ') : ['created_at', 'DESC'];
    const response = await learnerService.getMyLearnings({
      page,
      limit,
      search: searchTerm,
      type: typeFilter,
      sortField: sortField === 'title' ? 'name' : sortField,
      sortDirection
    });
    const filteredData = response.data.myLearnings || [];
    const pagination = response.data.pagination || { page: 1, limit, total: filteredData.length };
    const totalPages = Math.ceil(pagination.total / pagination.limit);
    const hasMore = pagination.page < totalPages;
  
    return {
      data: {
        sortedItems: filteredData,
        courses: typeFilter !== 'paths' ? filteredData.filter(item => item.type === 'course') : [],
        lpaths: typeFilter !== 'courses' ? filteredData.filter(item => item.type === 'learning path') : [],
        mandatory: typeFilter !== 'mandatory' ? filteredData.filter(item => item.type === 'mandatory') : [],
        compliance: typeFilter !== 'compliance' ? filteredData.filter(item => item.type === 'compliance') : [],
        // job_aids:typeFilter !== 'courses' && typeFilter!== 'paths' ? filteredData.filter(item=>item.type=== 'job aid') : [],
        //certificates:typeFilter !== 'courses' && typeFilter!== 'paths' && typeFilter!== 'jobAid' ? filteredData.filter(item=>item.type=== 'certificate') : [],
        pagination: {
          total: pagination.total,
          hasMore
        }
      }
    };
  }, [typeFilter]); // <-- Add typeFilter as dependency

  useEffect(() => {
    fetchMyLearnings();
    fetchRecentSearchContent();

    const handleItemGridSearchChange = (event) => {
      fetchRecentSearchContent(event.detail || '');
    };
    window.addEventListener('itemGridSearchChange', handleItemGridSearchChange);

    return () => window.removeEventListener('itemGridSearchChange', handleItemGridSearchChange);
  }, []);

  const fetchRecentSearchContent = async (searchTerm) => {
    try {
      setRecentContentLoading(true);
      const response = await learnerService.getRecentContent({ limit: 12, type: 'all', search: searchTerm });
      const contents = response.data.contents || [];
      setRecentSearchContent(contents.filter(item => !item.course_id));
    } catch (err) {
      console.error('Error fetching recent search content:', err);
      setRecentSearchContent([]);
    } finally {
      setRecentContentLoading(false);
    }
  };

  const fetchMyLearnings = async () => {
    try {
      setLoading(true);
      const response = await learnerService.getMyLearnings();
      setLearnings(response.data.myLearnings || []);
    } catch (err) {
      console.error('Error fetching my learnings:', err);
      setError('Failed to load your learning activities. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Filter learnings based on active filter
  const filteredLearnings = learnings.filter(item => {
    if (typeFilter === 'all') return true;
    if (typeFilter === 'courses') return item.type === 'course';
    if (typeFilter === 'paths') return item.type === 'learning path';
    if (typeFilter === 'jobAid') return item.type === 'job aid';
    //if (typeFilter === 'certificates') return item.type === 'certificate';
    return true;
  });
  
  if (loading) {
    return <LoadingSkeleton rows={6} />;
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', p: 4, color: 'error.main' }}>
        <Typography variant="h6">{error}</Typography>
      </Box>
    );
  }   

  const CustomFilterComponent = () => {
    
    // Hide type filter if embed setting is disabled
    if (isEmbedded && embedParams.hideMlTypeFilter) {
      return null;
    }

    return (
      <FormControl 
        sx={{
          minWidth: 200,
          maxWidth: 250,
          // height: 35,
          '& .MuiInputBase-root': {
            // height: 35,
            padding: '0 8px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiSelect-select': {
          padding: '5px 10px', 
          },
        }}
      >
        <Select
          value={typeFilter}
          onChange={(e) => { 
            setTypeFilter(e.target.value);
            if (e.target.value !== typeFilter) {
              // This will trigger a re-fetch with the new filter
              const event = new CustomEvent('resetItemGrid');
              window.dispatchEvent(event);
            }
          }}
           className="pink-filter"
          displayEmpty
        >
          <MenuItem value="all">All Types</MenuItem>
          <MenuItem value="courses">Courses</MenuItem>
          <MenuItem value="paths">Learning Paths</MenuItem>
          <MenuItem value="mandatory">Mandatory Courses</MenuItem>
          <MenuItem value="compliance">Compliance Courses</MenuItem>
          {/* <MenuItem value="job aid">Job Aids</MenuItem> */}
          {/* <MenuItem value="certificates">Certificate</MenuItem> */}
        </Select>
      </FormControl>
    );
  };
  return (
    <div className="my-learnings-container">
      <ItemGrid
        title="My Learnings"
        description=""
        itemType={
        typeFilter === 'courses'
          ? 'course'
          : typeFilter === 'paths'
          ? 'learningPath'
          // : typeFilter === 'jobAid'
          // ? 'job aid'
          : typeFilter === 'certificates'
          ? 'certificate'
          : 'all'
        }
        fetchItems={fetchItems}
        ItemCardComponent={MyLearningCard}
        ItemListComponent={MyLearningListView}
        createPermission=""
        createPath=""
        filterOptions={true}
        authorFilterEnabled={false}
        CustomFilterComponent={CustomFilterComponent}
        isEmbedded={isEmbedded}
        showSortingFilter={isEmbedded ? !embedParams.hideMlSortingFilter : true}
        showViewToggle={isEmbedded ? !embedParams.hideMlGridList : true}
        embedParams={embedParams}
        gridExtraItems={
          typeFilter === 'all' && recentSearchContent.length > 0 && !recentContentLoading
            ? recentSearchContent.map((item) => (
                <SearchContentCard
                  key={`search-${item.id}`}
                  item={item}
                  onOpen={(contentId) => openContentPlayer(null, contentId, '/', null, true, null, null, null, null, null, null, null, true)}
                />
              ))
            : null
        }
        hasExtraSearchResults={recentSearchContent.length > 0}
        //onClearFilters={() => window.dispatchEvent(new CustomEvent('resetItemGrid'))}
        hasActiveCustomFilters={typeFilter !== 'all'}
        onClearFilters={() => setTypeFilter('all')}
      />
    </div>
  );
};

export default MyLearnings;